/**
 * ParentSessionRow — one row in the Reports-tab session list.
 *
 * Implements US-011 (T3). Extracted from the inline map in
 * ParentDashboardPage.tsx (Reports tab, lines 558-578) so the per-row
 * logic (report-open, fraction, cancelled handling) is testable in isolation.
 *
 * Shows per session:
 *   AC-1: passage title, date, question count, 1-5 star rating
 *   AC-2: 'First-try N of M' fraction beside the stars (completed sessions only)
 *   AC-3: a 'View Report' button that opens the full HTML report in a new tab via
 *          GET /sessions/{sessionId}/report-url (parent-authorized by US-011 T1);
 *          only shown when session.reportAvailable === true
 *   AC-4: cancelled sessions render no star, no fraction, and a 'Cancelled' badge
 *
 * CCM-01: reads session.status and handles 'cancelled' correctly — no star, no
 *   fraction, 'Cancelled' label (CCM-01 invariant for status readers).
 *
 * Reuses:
 *   - StarRating (US-018 T3) verbatim — returns null for null/<=0 rating (AC-4)
 *   - getReportUrl (services/reports.ts) verbatim — now parent-authorized by T1
 *   - popup-blocker-safe window.open pattern from SessionCard.tsx:63-110 verbatim
 *   - Button + the two-button row layout from SessionCard.tsx:198-224
 *
 * Enforces TO-LL-036 (fail-closed error handling — report-open failures surface as
 *   inline row error, the blank tab is closed, never swallowed).
 * Enforces TO-LL-044 (camelCase field reads match the backend contract exactly).
 * Enforces TO-LL-058 (absolute '@/' imports only — no relative path imports).
 *
 * Modified TS-007 (T11, SR6, AC-TS007.6b): added an isEndedByParent branch, cloned
 *   verbatim from the existing isCancelled branch below (same no-star/no-fraction
 *   treatment), rendering 'Ended early' for status === 'ended_by_parent'.
 *
 * Modified (report-conversation-removal): the whole-row tap affordance was replaced by
 *   two explicit buttons, mirroring the student's SessionCard — 'View Session' (read-only
 *   transcript at ROUTES.PARENT_SESSION) and 'View Report'. The transcript button exists
 *   because the generated report no longer embeds the per-question conversation. It is
 *   offered for EVERY status, including cancelled/ended_by_parent rows that previously had
 *   no affordance at all: those sessions have no report, but their transcript is exactly
 *   what a parent wants to read to understand what happened.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StarRating } from '@/components/StarRating';
import { parentSessionPath } from '@/constants/routes';
import { getReportUrl } from '@/services/reports';
import type { ParentSession } from '@/types/coins';

/**
 * Props for ParentSessionRow.
 */
export interface ParentSessionRowProps {
  /** A single parent-accessible session entry from useParentSessions. */
  session: ParentSession;
}

/**
 * ParentSessionRow renders one row in the Reports-tab session list.
 *
 * Implements US-011 (T3); enforces TO-LL-036, TO-LL-044, TO-LL-058.
 * CCM-01: recognises 'cancelled' status as a valid session state.
 */
export function ParentSessionRow({ session }: ParentSessionRowProps) {
  const navigate = useNavigate();

  // Local state for the report-open flow (mirrors SessionCard.tsx:60-61).
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Derive status booleans — CCM-01: 'cancelled' is a recognised session status.
  const isCancelled = session.status === 'cancelled';
  const isCompleted = session.status === 'completed';
  // TS-007 (T11, SR6): 'ended_by_parent' is ALSO a recognised terminal status —
  // cloned treatment from isCancelled (no star, no fraction, non-tappable).
  const isEndedByParent = session.status === 'ended_by_parent';

  // Offer the report only when one has been generated (AC-3).
  const canOpenReport = isCompleted && session.reportAvailable === true;

  /**
   * Open the full HTML session report in a new tab.
   *
   * Uses the popup-blocker-safe pattern from SessionCard.tsx:63-110:
   * 1. Open a blank tab synchronously INSIDE the user gesture (before any await).
   * 2. Sever the opener reference for security.
   * 3. Await the presigned URL from the backend.
   * 4. Assign the URL to the pre-opened tab.
   * 5. On any failure: close the blank tab and show an inline error (TO-LL-036).
   *
   * Implements US-011 (T3); enforces TO-LL-036.
   */
  const handleOpenReport = async () => {
    if (!canOpenReport || isLoadingReport) return;

    setIsLoadingReport(true);
    setReportError(null);

    // Step 1: Pre-open a blank tab synchronously BEFORE any await.
    // BUG-20260616-202933 pattern: do NOT pass 'noopener' — that returns null.
    const reportTab = window.open('', '_blank');

    // Step 2: If null, popup was blocked — surface an inline error and bail.
    if (reportTab === null) {
      setReportError('Please allow pop-ups to view the report.');
      setTimeout(() => setReportError(null), 3000);
      setIsLoadingReport(false);
      return;
    }

    // Step 3: Sever the opener reference — preserves no-opener security posture
    // without losing the tab handle (mirrors SessionCard.tsx:85).
    reportTab.opener = null;

    try {
      const { reportUrl } = await getReportUrl(session.sessionId);
      // Step 4: Assign the resolved URL to the pre-opened tab.
      try {
        reportTab.location.href = reportUrl;
      } catch {
        // Rare: user closed the blank tab before we could assign.
        reportTab.close();
        setReportError('Failed to open report. Please try again.');
        setTimeout(() => setReportError(null), 3000);
      }
    } catch (error: unknown) {
      // Step 5: Close the blank tab — no stray empty tab on failure (TO-LL-036).
      reportTab.close();
      const message =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to load report. Please try again.';
      setReportError(message);
      setTimeout(() => setReportError(null), 3000);
    } finally {
      setIsLoadingReport(false);
    }
  };

  /** Open the read-only transcript of this session (parent-scoped route). */
  const handleViewSession = () => {
    navigate(parentSessionPath(session.sessionId));
  };

  // Format date as 'D Mon' (e.g. '12 Jun') using Singapore locale.
  const formattedDate = session.date
    ? new Date(session.date).toLocaleDateString('en-SG', {
        day: 'numeric',
        month: 'short',
      })
    : null;

  // Question count label (AC-1).
  const questionLabel =
    session.questionCount === 1 ? '1 question' : `${session.questionCount} questions`;

  // First-try fraction (AC-2): only for completed sessions with eligible questions.
  const showFraction = isCompleted && (session.firstTryTotal ?? 0) > 0;

  // Label the actions with the passage title so screen-reader users can tell
  // one row's buttons from the next (the row itself is no longer a button).
  const sessionLabel = session.passageTitle || 'session';

  return (
    <div className="rounded-xl bg-white border border-gray-200 p-3 flex flex-col gap-1">
      {/* Main row: left info + right rating/status */}
      <div className="flex items-center justify-between gap-3">
        {/* Left: passage title + metadata */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-800 truncate">
            {session.passageTitle || 'Session'}
          </p>
          <p className="text-xs text-gray-400">
            {session.childDisplayName}
            {formattedDate ? ` · ${formattedDate}` : ''}
            {` · ${questionLabel}`}
          </p>
        </div>

        {/* Right: rating/status column */}
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          {isCancelled ? (
            /* AC-4: cancelled sessions show NO star, NO fraction — only a label */
            <span className="text-xs text-gray-400 italic">Cancelled</span>
          ) : isEndedByParent ? (
            /* TS-007 (T11, AC-TS007.6b): cloned from the isCancelled branch above —
               ended_by_parent sessions show NO star, NO fraction — only a label. */
            <span className="text-xs text-gray-400 italic">Ended early</span>
          ) : (
            <>
              {/* AC-1: star rating — StarRating returns null for null/<=0 (unrated) */}
              <StarRating rating={session.starRating} size="sm" />
              {/* AC-2: first-try fraction beside the stars (completed only) */}
              {showFraction && (
                <span className="text-xs text-gray-500">
                  First-try {session.firstTryCorrect} of {session.firstTryTotal}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Actions — mirrors the student's SessionCard two-button row.
          'View Session' is offered for every status (the transcript always exists);
          'View Report' only once a report has been generated (AC-3). */}
      <div className="flex gap-2 mt-1.5">
        <Button
          variant="warm"
          size="sm"
          onClick={handleViewSession}
          className="flex-1 h-11"
          aria-label={`View session transcript for ${sessionLabel}`}
        >
          View Session
        </Button>
        {canOpenReport && (
          <Button
            variant="warm"
            size="sm"
            onClick={() => void handleOpenReport()}
            disabled={isLoadingReport}
            aria-busy={isLoadingReport}
            aria-label={`View report for ${sessionLabel}`}
            className="flex-1 h-11 bg-transparent border-2 border-otter-orange-500 text-otter-orange-600 hover:bg-otter-orange-50"
          >
            {isLoadingReport ? 'Loading...' : 'View Report'}
          </Button>
        )}
      </div>

      {/* Inline report error (TO-LL-036 — failures never swallowed) */}
      {reportError && (
        <p className="text-xs text-red-500 mt-0.5 text-center">{reportError}</p>
      )}
    </div>
  );
}
